/** Regras de apresentação do portal: contatos acionáveis e duração já apurada pelo servidor. */
import { normalizePhoneIdentifier, isSpecialPhoneNumber } from '@/lib/phone-utils';
import { isStockStatusOperational } from '@/lib/stock-presentation';

/** Resultado oficial da pontualidade: nulo significa que o histórico não permite comparar. */
export interface ProviderPunctuality { delay_seconds: number | null; on_time: boolean | null; scheduled_reference?: string | null }

/** Percentuais não transformam bases vazias em sucesso ou falha fictícios. */
export function providerCompletionPercent(done?: number, total?: number): number | null {
  if (done == null || total == null || !Number.isFinite(done) || !Number.isFinite(total) || total <= 0) return null;
  return Math.round(Math.min(total, Math.max(0, done)) / total * 100);
}

/** Detalhe e resumo compartilham o resultado definitivo emitido pelo servidor. */
export function providerPunctualityLabel(value?: ProviderPunctuality | null): string {
  if (value?.on_time === true) return 'No prazo';
  if (value?.on_time === false && value.delay_seconds != null && Number.isFinite(value.delay_seconds) && value.delay_seconds >= 0) return providerScheduleDelayDuration(value.delay_seconds);
  return 'Não registrado';
}

/** Atraso para chegada, separado da duração produtiva e da permanência no local. */
export function providerScheduleDelay({ scheduledStart, checkedInAt, status, now }: {
  scheduledStart?: string | null;
  checkedInAt?: string | null;
  status: string;
  now: number;
}): number | null {
  if (!scheduledStart || checkedInAt || !['open', 'scheduled', 'in_progress'].includes(status)) return null;
  const start = Date.parse(scheduledStart);
  if (!Number.isFinite(start) || !Number.isFinite(now) || now <= start) return null;
  return Math.floor((now - start) / 1000);
}

/** Mantém o atraso compacto na coluna de agendamento, sem perder dias inteiros. */
export function providerScheduleDelayDuration(seconds: number): string {
  const minutes = Math.floor(Math.max(0, seconds) / 60);
  if (minutes < 1) return 'Menos de 1 min';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h${minutes % 60 ? ` ${minutes % 60} min` : ''}`;
  return `${Math.floor(hours / 24)} d${hours % 24 ? ` ${hours % 24} h` : ''}`;
}

/** Dados mínimos para contar o estoque sem incluir novamente componentes de kits. */
export interface ProviderInventorySummaryItem {
  kind: 'kit' | 'tracker' | 'chip';
  status?: string | null;
  tracker_status?: string | null;
  chip_status?: string | null;
}

/** Consulta visual, não autorização de instalação: kit bloqueado conta apenas uma vez. */
export function isProviderInventoryUnavailable(item: ProviderInventorySummaryItem): boolean {
  return !isStockStatusOperational(item.kind, item.status)
    || (item.kind === 'kit' && (!isStockStatusOperational('tracker', item.tracker_status)
      || !isStockStatusOperational('chip', item.chip_status)));
}

/** Total e indicadores por tipo usam somente os itens de primeiro nível da resposta oficial. */
export function providerInventorySummary(items: ProviderInventorySummaryItem[]) {
  return {
    tracker: items.filter((item) => item.kind === 'tracker').length,
    chip: items.filter((item) => item.kind === 'chip').length,
    kit: items.filter((item) => item.kind === 'kit').length,
    all: items.length,
    unavailable: items.filter(isProviderInventoryUnavailable).length,
  };
}

/** O filtro compartilha exatamente o predicado usado no indicador Indisponíveis. */
export function matchesProviderInventoryFilter(item: ProviderInventorySummaryItem, filter: 'all' | 'kit' | 'tracker' | 'chip' | 'unavailable'): boolean {
  return filter === 'all' || (filter === 'unavailable' ? isProviderInventoryUnavailable(item) : item.kind === filter);
}

/** Nunca transforma telefone mascarado ou texto arbitrário em destino de contato. */
export function providerContactLinks(phone: string | null | undefined) {
  if (!phone || phone.length > 30 || !/^[+\d()\s-]+$/.test(phone)) return null;
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 10 || digits.length > 15) return null;
  const normalized = normalizePhoneIdentifier(phone);
  return {
    call: `tel:${isSpecialPhoneNumber(digits) ? digits : `+${normalized}`}`,
    whatsapp: isSpecialPhoneNumber(digits) ? null : `https://wa.me/${normalized}`,
  };
}

/** O cálculo líquido permanece no banco; aqui apenas convertemos segundos para minutos. */
export function providerDuration(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return 'Não registrado';
  const minutes = Math.floor(seconds / 60);
  if (!minutes && seconds > 0) return 'Menos de 1 min';
  return minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
}

/** Tempo de permanência no local: independe de pausas e não substitui o tempo líquido oficial. */
export function providerCheckInClock({ checkedInAt, finishedAt, status, now = Date.now() }: {
  checkedInAt?: string | null;
  finishedAt?: string | null;
  status: string;
  now?: number;
}): { state: 'waiting' | 'running' | 'finished' | 'unavailable'; seconds: number | null } {
  if (!checkedInAt) return { state: 'waiting', seconds: null };
  const start = Date.parse(checkedInAt);
  if (!Number.isFinite(start)) return { state: 'unavailable', seconds: null };
  // Estados sem execução corrente não podem incluir revisão/correção nem inventar término.
  const stopped = ['awaiting_review', 'completed', 'in_correction', 'cancelled'].includes(status);
  const finish = finishedAt ? Date.parse(finishedAt) : null;
  if ((finishedAt && !Number.isFinite(finish)) || (stopped && finish == null)) {
    return { state: 'unavailable', seconds: null };
  }
  const end = finish ?? now;
  if (!Number.isFinite(end)) return { state: 'unavailable', seconds: null };
  return { state: finish == null ? 'running' : 'finished', seconds: Math.max(0, Math.floor((end - start) / 1000)) };
}

/** Horas sem limite de 24h e algarismos estáveis para o cronômetro de permanência. */
/** A pausa muda apenas a indicação; término e ausência de check-in têm prioridade. */
export function providerClockActivity(state: 'waiting' | 'running' | 'finished' | 'unavailable', isPaused: boolean) {
  return state === 'running' ? (isPaused ? 'paused' : 'active') : state;
}

export function providerClockDuration(seconds: number | null): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return 'Não registrado';
  const total = Math.floor(seconds);
  return [Math.floor(total / 3600), Math.floor(total / 60) % 60, total % 60]
    .map((part) => String(part).padStart(2, '0')).join(':');
}
/** Distância Haversine em metros: mesma fórmula de _geo_distance_m (o servidor decide). */
export function geoDistanceM(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const rad = (v: number) => (v * Math.PI) / 180;
  const a = Math.sin(rad(lat2 - lat1) / 2) ** 2 + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(rad(lng2 - lng1) / 2) ** 2;
  return Math.round(2 * 6371000 * Math.asin(Math.sqrt(a)) * 10) / 10;
}

export type ArrivalState = 'no_reference' | 'poor_accuracy' | 'outside' | 'inside';

/** Estado exibido na tela de chegada; prioridade: precisão ruim, sem referência, fora/dentro. */
export function providerArrivalState({ accuracy, distance, radius, maxAccuracy }: {
  accuracy: number; distance: number | null; radius: number; maxAccuracy: number;
}): ArrivalState {
  if (accuracy > maxAccuracy) return 'poor_accuracy';
  if (distance == null) return 'no_reference';
  return distance <= radius ? 'inside' : 'outside';
}

/** Linhas gravadas na marca d'água da foto de chegada. */
export function arrivalWatermarkLines({ at, lat, lng, accuracy, destination, distance, orderNumber, providerName }: {
  at: Date; lat: number; lng: number; accuracy: number; destination: { lat: number; lng: number } | null;
  distance: number | null; orderNumber: string; providerName?: string;
}): string[] {
  const stamp = at.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  return [
    [orderNumber, providerName, stamp].filter(Boolean).join(' · '),
    `Aparelho: ${lat.toFixed(6)}, ${lng.toFixed(6)} (±${Math.round(accuracy)} m)`,
    destination ? `OS: ${destination.lat.toFixed(6)}, ${destination.lng.toFixed(6)} · Distância: ${Math.round(distance ?? 0)} m` : 'OS sem coordenadas de referência',
  ];
}

/** Idade máxima (s) aceita localmente para reaproveitar a última leitura do GPS (servidor aceita até 60 s). */
export const PROVIDER_POSITION_MAX_LOCAL_AGE_S = 45;

/**
 * Calcula a idade (segundos inteiros) de uma leitura de GPS pelo horário da própria leitura.
 * Nunca retorna negativo (relógio do aparelho pode adiantar o timestamp).
 */
export function providerPositionAgeS(timestamp: number, now: number = Date.now()): number {
  if (!Number.isFinite(timestamp)) return Number.POSITIVE_INFINITY;
  return Math.max(0, Math.round((now - timestamp) / 1000));
}

/** Indica se a leitura ainda pode ser usada como reserva quando a leitura nova falhar. */
export function providerPositionReusable(timestamp: number, now: number = Date.now()): boolean {
  return providerPositionAgeS(timestamp, now) < PROVIDER_POSITION_MAX_LOCAL_AGE_S;
}

/**
 * Situações de checklist já entregues pelo prestador (RELEASE 2026-10-17).
 * Antecipa no app a regra do servidor que bloqueia a conclusão com checklist em aberto.
 */
export const PROVIDER_CHECKLIST_DONE = ['awaiting_review', 'approved', 'finished'];

/** Checklists ainda não entregues (pendente, em andamento, correção solicitada etc.). */
export function pendingChecklists<T extends { status: string }>(items: T[]): T[] {
  return items.filter((item) => !PROVIDER_CHECKLIST_DONE.includes(item.status) && item.status !== 'cancelled');
}

/** Link externo do Google Maps para um ponto registrado (sem custo, sem chave). */
export function googleMapsPointUrl(lat: number, lng: number): string {
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
}

/** Lê {latitude, longitude, accuracy} gravado no evento oficial (new_value JSON). */
export function parseEventLocation(raw: string | null | undefined): { lat: number; lng: number; accuracy: number | null } | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as { latitude?: unknown; longitude?: unknown; accuracy?: unknown };
    const lat = Number(v.latitude); const lng = Number(v.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
    const acc = Number(v.accuracy);
    return { lat, lng, accuracy: Number.isFinite(acc) ? acc : null };
  } catch { return null; }
}

/** Tempo de deslocamento (s) do início do deslocamento até o check-in; nulo se faltar ou for negativo. */
export function providerTravelSeconds(travelStartedAt: string | null | undefined, checkedInAt: string | null | undefined): number | null {
  if (!travelStartedAt || !checkedInAt) return null;
  const a = new Date(travelStartedAt).getTime(); const b = new Date(checkedInAt).getTime();
  if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return null;
  return Math.round((b - a) / 1000);
}

/** v1.x — Converte a média em segundos do servidor para minutos; base vazia devolve null (sem ponto fictício). */
export function providerAvgMinutes(seconds: number | null | undefined, count: number | null | undefined): number | null {
  if (!count || seconds == null || !Number.isFinite(Number(seconds))) return null;
  return Math.round(Number(seconds) / 60);
}

/** Formata minutos médios: "45 min" ou "1 h 05 min"; sem base mostra "—". */
export function formatProviderAvg(minutes: number | null): string {
  if (minutes == null) return '—';
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')} min`;
}
